import { PlaybookStatus } from '@driftt/api-types'
import { A, Button, Colorway, Div, Tag } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { Modal, ModalBody, ModalTitle } from '@driftt/tide-modal'
import { pluralize } from 'humanize-plus'
import React from 'react'

import { HydratedPlaybookBlockData } from '.'
import CollapsibleArchivedList from './CollapsibleArchivedList'
import { splitArchivedPlaybooks } from './helpers'

import styles from './styles.module.css'

interface Props {
  isOpen: boolean
  onClose: () => void
  consumingPlaybooks: HydratedPlaybookBlockData[]
}

const UsedInPlaybooksModal = ({
  isOpen,
  onClose,
  consumingPlaybooks,
}: Props) => {
  const { archived, active } = React.useMemo(
    () => splitArchivedPlaybooks(consumingPlaybooks),
    [consumingPlaybooks]
  )

  return (
    <Modal open={isOpen} onClose={onClose} size="small">
      <ModalTitle>
        Block used in {active.length || 0}{' '}
        {pluralize(active.length, 'playbook')}
      </ModalTitle>
      <ModalBody>
        {consumingPlaybooks.length > 0 ? (
          <>
            {active.length > 0 && (
              <>
                <Div className={styles.usedInModalSubheader}>
                  {pluralize(active.length, 'Playbook')} where this block is
                  used:
                </Div>
                <Div className={styles.usedInModalPbList}>
                  {active.map(({ playbook: pb, playbookStatus }, index) => {
                    return (
                      <Div key={index} className={styles.pbRow}>
                        <Div className={styles.pbDetails}>
                          <Tag
                            wrapperClass={styles.pbStatus}
                            type="status"
                            statusIsOn={
                              playbookStatus === PlaybookStatus.ENABLED
                            }
                          />
                          <A
                            href={`/playbooks/editor/${pb.id}/visual-builder`}
                            className={styles.pbName}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {pb.name}
                          </A>
                        </Div>
                        <Div className={styles.versionUpToDate}>Up to date</Div>
                      </Div>
                    )
                  })}
                </Div>
              </>
            )}
            {archived.length > 0 && (
              <CollapsibleArchivedList
                archivedPlaybooks={archived}
                title={`Used in ${archived.length} archived ${pluralize(
                  archived.length,
                  'playbook'
                )}`}
              />
            )}
          </>
        ) : (
          <Div className={styles.modalEmptyState}>
            <Colorway className={styles.modalEmptyStateColorway} type="2">
              <Icon name="cw-bots" width="80" height="80" />
            </Colorway>
            <Div className={styles.modalEmptyStateSubheader}>
              This block isn't used in any playbooks yet!
            </Div>
            Open a playbook to insert this block anywhere in the bot flow.
            <Button
              className={styles.modalEmptyStateButton}
              target="_blank"
              href="/playbooks"
            >
              View playbooks
            </Button>
          </Div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default UsedInPlaybooksModal
